// src/features/equipment/pages/EquipmentDetailsPage/tabs/index.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  Assignment as AssignmentIcon,
  AttachFile as AttachFileIcon,
  Build as BuildIcon,
} from '@mui/icons-material';

import EquipmentDetailsCard from '../components/EquipmentDetailsCard';
import EquipmentMetadataCard from '../components/EquipmentMetadataCard';
import EquipmentImageCard from '../components/EquipmentImageCard';
import EquipmentTasksCard from '../components/EquipmentTasksCard';
import EquipmentFilesCard from '../components/EquipmentFilesCard';
import EquipmentMaintenanceTab from '../../../components/maintenance/EquipmentMaintenanceTab';

function TabPanel({ children, value, index, sx }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`equipment-tabpanel-${index}`}
      aria-labelledby={`equipment-tab-${index}`}
      sx={value !== index ? null : { ...sx, minHeight: 0 }}
    >
      {value === index && <Box sx={{ minHeight: 0, ...sx }}>{children}</Box>}
    </Box>
  );
}

function a11yProps(index) {
  return {
    id: `equipment-tab-${index}`,
    'aria-controls': `equipment-tabpanel-${index}`,
  };
}

export default function EquipmentTabs({
  equipmentId,
  equipment,
  maintenanceActions,
  maintenanceRefreshKey,
  onOpenMaintenanceCase,
}) {
  const { t } = useTranslation(['equipment', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const tabs = [
    {
      key: 'details',
      label: t('equipment:tabs.details.title'),
      description: t('equipment:tabs.details.description'),
      icon: InfoIcon,
    },
    {
      key: 'tasks',
      label: t('equipment:tabs.tasks.title'),
      description: t('equipment:tabs.tasks.description'),
      icon: AssignmentIcon,
    },
    {
      key: 'maintenance',
      label: t('equipment:tabs.maintenance.title'),
      description: t('equipment:tabs.maintenance.description'),
      icon: BuildIcon,
    },
    {
      key: 'files',
      label: t('equipment:tabs.files.title'),
      description: t('equipment:tabs.files.description'),
      icon: AttachFileIcon,
    },
  ];

  const getTabFromParams = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const idx = tabs.findIndex(tab => tab.key === tabParam);
      return idx !== -1 ? idx : 0;
    }
    return 0;
  };

  const [currentTab, setCurrentTab] = useState(getTabFromParams);

  useEffect(() => {
    setCurrentTab(getTabFromParams());
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (_, newValue) => {
    setCurrentTab(newValue);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tabs[newValue].key);
    newParams.delete('case');
    setSearchParams(newParams);
  };

  return (
    <Box sx={{ width: '100%', mt: 3, flex: 1, minHeight: 0 }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
          height: '100%',
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="equipment detail tabs"
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              minHeight: isMobile ? 56 : 64,
              py: isMobile ? 1.5 : 2,
              px: isMobile ? 1.5 : 3,
              '&.Mui-selected': { fontWeight: 600, bgcolor: 'background.paper' },
            },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            return (
              <Tab
                key={tab.key}
                icon={<Icon sx={{ fontSize: isMobile ? 34 : 36 }} />}
                iconPosition="start"
                label={
                  <Box sx={{ textAlign: 'left', ml: isMobile ? 0.5 : 1, pb: 1 }}>
                    <Typography variant="body2" fontWeight="inherit">{tab.label}</Typography>
                    {!isMobile && (
                      <Typography variant="caption" color="text.secondary">{tab.description}</Typography>
                    )}
                  </Box>
                }
                sx={{ alignItems: 'center', justifyContent: 'flex-start', minWidth: 'unset', height: '100%' }}
                {...a11yProps(index)}
              />
            );
          })}
        </Tabs>

        <Box sx={{ bgcolor: 'background.paper', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Details Tab */}
          <TabPanel value={currentTab} index={0} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box p={3} display="flex" gap={3} flexDirection={{ xs: 'column', lg: 'row' }}>
              <Box display="flex" flexDirection="column" gap={3} flex={2}>
                <EquipmentDetailsCard equipment={equipment} />
                <EquipmentMetadataCard equipment={equipment} />
              </Box>
              <Box display="flex" flexDirection="column" gap={3} flex={1}>
                <EquipmentImageCard equipment={equipment} />
              </Box>
            </Box>
          </TabPanel>

          {/* Tasks Tab */}
          <TabPanel value={currentTab} index={1} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <EquipmentTasksCard equipmentId={equipmentId} equipment={equipment} />
          </TabPanel>

          {/* Maintenance Tab */}
          <TabPanel value={currentTab} index={2} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <EquipmentMaintenanceTab
              equipment={equipment}
              refreshKey={maintenanceRefreshKey}
              actions={maintenanceActions}
              onOpenCase={onOpenMaintenanceCase}
            />
          </TabPanel>

          {/* Files Tab */}
          <TabPanel value={currentTab} index={3} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <EquipmentFilesCard equipmentId={equipmentId} equipment={equipment} />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
}
